import { type CoreIcon } from '@pollux-docaposte/core-design-system'
import { Component, Host, h, Prop, Event, type EventEmitter } from '@stencil/core'

@Component({
  tag: 'cobalt-button',
  styleUrl: 'cobalt-button.css'
})
export class CobaltButton {
  /**
   * This is the text which appear inside the button. It is required.
   */
  @Prop() label!: string
  /**
   * A string indicating the behavior of the button. It relies on `HTMLButtonElement['type']`
   */
  @Prop() type?: HTMLButtonElement['type'] = 'button'
  /**
   * A string indicating the design variation of the button based on the level of importance.
   */
  @Prop() variant?: 'primary' | 'secondary' | 'tertiary' = 'primary'
  /**
   * A boolean indicating the disable state of the button. The `aria-disabled` attribute relies on this property.
   */
  @Prop({ mutable: true }) disabled?: boolean = false
  /**
   * A string indicating the size variation of the button.
   */
  @Prop({ mutable: true }) size?: 'small' | 'medium' | 'large' = 'medium'
  /**
   * Accept all icon names from @pollux-docaposte/icon-library. By using the cobalt-button component you don't have to install it as dependency.
   *
   * The icon render only if `iconName` and `iconPosition` are defined.
   */
  @Prop({ mutable: true }) iconName?: CoreIcon['name'] = undefined
  /**
   * It allows to render the chosen icon on the left or on the right.
   *
   * The icon render only if `iconName` and `iconPosition` are defined.
   */
  @Prop({ mutable: true }) iconPosition?: 'left' | 'right' = undefined

  /**
   * Click event on the button
   */
  @Event() cobaltButtonClick: EventEmitter

  /**
   * Handle the click event on the button.
   */
  private readonly handleClick = (ev: Event): void => {
    if (this.disabled) {
      ev.preventDefault()
      ev.stopPropagation()
      return
    }
    this.cobaltButtonClick.emit()
  }

  render(): any {
    return (
      <Host>
        <core-button
          type={this.type}
          label={this.label}
          variant={this.variant}
          size={this.size}
          disabled={this.disabled}
          icon-name={this.iconName}
          icon-position={this.iconPosition}
          onCoreButtonClick={this.handleClick}
        ></core-button>
      </Host>
    )
  }
}
